import {
  Box,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import SyncFrequencySelect from "../components/SyncFrequencySelect";

interface WebsiteIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    url?: string;
    maxPages?: number;
    maxDepth?: number;
    syncFrequency?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    url: string;
    maxPages: number;
    maxDepth: number;
    syncFrequency: string;
  }) => void;
}

export default function WebsiteIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
}: WebsiteIntegrationFormProps) {
  const [url, setUrl] = useState(initialData?.url || "");
  const [maxPages, setMaxPages] = useState(initialData?.maxPages || 25);
  const [maxDepth, setMaxDepth] = useState(initialData?.maxDepth || 3);
  const [syncFrequency, setSyncFrequency] = useState(
    initialData?.syncFrequency || "manual"
  );

  // Validate URL format
  const isValidUrl = (urlString: string) => {
    try {
      const url = new URL(urlString);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  };

  // Notify parent of validation status
  useEffect(() => {
    const isFormValid = url.trim() !== "" && isValidUrl(url);
    onValidationChange(isFormValid);
  }, [url, onValidationChange]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      url,
      maxPages,
      maxDepth,
      syncFrequency,
    });
  }, [url, maxPages, maxDepth, syncFrequency, onDataChange]);

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        fullWidth
        value={url}
        label="Website URL"
        placeholder="https://example.com"
        helperText="Enter the URL of the website you want to crawl"
        onChange={(e) => setUrl(e.target.value)}
        sx={{ mb: 3 }}
        error={url !== "" && !isValidUrl(url)}
      />

      <Typography gutterBottom>
        Max Pages: {maxPages}
      </Typography>
      <Slider
        value={maxPages}
        onChange={(e, newValue) => setMaxPages(newValue as number)}
        min={1}
        max={100}
        valueLabelDisplay="auto"
        sx={{ mb: 3 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Maximum number of pages to crawl from the website
      </Typography>

      <Typography gutterBottom>
        Max Depth: {maxDepth}
      </Typography>
      <Slider
        value={maxDepth}
        onChange={(e, newValue) => setMaxDepth(newValue as number)}
        min={1}
        max={6}
        valueLabelDisplay="auto"
        sx={{ mb: 3 }}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Maximum depth of links to follow from the starting URL
      </Typography>

      {/* Auto-Sync Frequency */}
      <SyncFrequencySelect
        value={syncFrequency}
        onChange={setSyncFrequency}
        sx={{ mt: 2 }}
      />
    </Box>
  );
}